import { useRef, useEffect, useCallback } from 'react';
import { trackEvents, trackEventsKeepalive, getToken } from '../auth/api';

const FLUSH_INTERVAL = 5000;
const HEARTBEAT_INTERVAL = 60000;
const SESSION_TIMEOUT = 30 * 60 * 1000;
const ACTIVE_WINDOW = 90 * 1000;

const STORAGE_KEYS = {
  id: 'analytics_session_id',
  user: 'analytics_session_user',
  lastActivity: 'analytics_session_last_activity',
  started: 'analytics_session_started',
};

function generateUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

export function useTracking(user) {
  const queueRef = useRef([]);
  const sessionIdRef = useRef(null);
  const lastActivityRef = useRef(Number(sessionStorage.getItem(STORAGE_KEYS.lastActivity)) || Date.now());

  const enqueue = useCallback((eventName, context = {}, sessionId = sessionIdRef.current) => {
    if (!getToken() || !sessionId) return;
    queueRef.current.push({
      event_id: generateUuid(),
      event_name: eventName,
      event_version: 2,
      session_id: sessionId,
      screen: context.screen,
      section: context.section,
      tool: context.tool,
      occurred_at: new Date().toISOString(),
      properties: context.properties,
    });
  }, []);

  const createSession = useCallback((previousId = null) => {
    if (!user?.id || !getToken()) return null;
    if (previousId) {
      enqueue('session_ended', { properties: { reason: 'idle' } }, previousId);
    }
    const sessionId = generateUuid();
    const now = Date.now();
    sessionIdRef.current = sessionId;
    lastActivityRef.current = now;
    sessionStorage.setItem(STORAGE_KEYS.id, sessionId);
    sessionStorage.setItem(STORAGE_KEYS.user, String(user.id));
    sessionStorage.setItem(STORAGE_KEYS.lastActivity, String(now));
    sessionStorage.setItem(STORAGE_KEYS.started, sessionId);
    enqueue('session_started', {}, sessionId);
    return sessionId;
  }, [enqueue, user?.id]);

  const ensureSession = useCallback((markActivity = false) => {
    if (!user?.id || !getToken()) return null;
    const now = Date.now();
    const storedId = sessionStorage.getItem(STORAGE_KEYS.id);
    const storedUser = sessionStorage.getItem(STORAGE_KEYS.user);
    const storedLast = Number(sessionStorage.getItem(STORAGE_KEYS.lastActivity)) || 0;
    const sameUser = storedUser === String(user.id);
    const expired = sameUser && storedId && now - storedLast > SESSION_TIMEOUT;

    let sessionId = storedId;
    if (!sameUser || !storedId || expired) {
      sessionId = createSession(expired ? storedId : null);
    } else {
      sessionIdRef.current = storedId;
      if (sessionStorage.getItem(STORAGE_KEYS.started) !== storedId) {
        sessionStorage.setItem(STORAGE_KEYS.started, storedId);
        enqueue('session_started', {}, storedId);
      }
    }

    if (markActivity && sessionId) {
      lastActivityRef.current = now;
      sessionStorage.setItem(STORAGE_KEYS.lastActivity, String(now));
    }
    return sessionId;
  }, [createSession, enqueue, user?.id]);

  const flush = useCallback(async () => {
    if (queueRef.current.length === 0) return;
    const batch = queueRef.current.splice(0, queueRef.current.length);
    try {
      await trackEvents(batch);
    } catch {
      queueRef.current = [...batch, ...queueRef.current].slice(0, 200);
    }
  }, []);

  const trackEvent = useCallback((eventName, context = {}) => {
    const sessionId = ensureSession(true);
    if (sessionId) enqueue(eventName, context, sessionId);
  }, [enqueue, ensureSession]);

  const endSession = useCallback(async (reason = 'logout') => {
    const sessionId = ensureSession(false);
    if (sessionId) {
      enqueue('session_ended', { properties: { reason } }, sessionId);
      await flush();
    }
    Object.values(STORAGE_KEYS).forEach((key) => sessionStorage.removeItem(key));
    sessionIdRef.current = null;
  }, [enqueue, ensureSession, flush]);

  useEffect(() => {
    if (user?.id) ensureSession(false);
  }, [ensureSession, user?.id]);

  useEffect(() => {
    if (!user?.id) return undefined;
    const markActivity = () => ensureSession(true);
    const events = ['pointerdown', 'keydown', 'input', 'scroll'];
    events.forEach((name) => window.addEventListener(name, markActivity, { passive: true }));
    return () => events.forEach((name) => window.removeEventListener(name, markActivity));
  }, [ensureSession, user?.id]);

  useEffect(() => {
    const flushTimer = setInterval(flush, FLUSH_INTERVAL);
    const heartbeatTimer = setInterval(() => {
      if (!user?.id || document.visibilityState !== 'visible') return;
      if (Date.now() - lastActivityRef.current > ACTIVE_WINDOW) return;
      const sessionId = ensureSession(false);
      if (sessionId) enqueue('session_heartbeat', {}, sessionId);
    }, HEARTBEAT_INTERVAL);
    return () => {
      clearInterval(flushTimer);
      clearInterval(heartbeatTimer);
    };
  }, [enqueue, ensureSession, flush, user?.id]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    const handlePageHide = () => {
      if (queueRef.current.length > 0) {
        const batch = queueRef.current.splice(0, queueRef.current.length);
        trackEventsKeepalive(batch);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [flush]);

  return { trackEvent, flush, endSession };
}
